import React from 'react';
import { TrendingUp, Award } from 'lucide-react';

interface ScoreChartProps {
  scores: {
    overall: number;
    wcag2A: number;
    wcag2AA: number;
    wcag2AAA: number;
    section508: number;
  };
}

export const ScoreChart: React.FC<ScoreChartProps> = ({ scores }) => {
  // Data for the standards breakdown with modern colors
  const standardsData = [
    {
      name: 'WCAG A',
      fullName: 'WCAG 2.1 Level A',
      value: scores.wcag2A,
      fill: '#8b5cf6', // Purple
      lightFill: '#c4b5fd'
    },
    {
      name: 'WCAG AA',
      fullName: 'WCAG 2.1 Level AA',
      value: scores.wcag2AA,
      fill: '#3b82f6', // Blue
      lightFill: '#93c5fd'
    },
    {
      name: 'WCAG AAA',
      fullName: 'WCAG 2.1 Level AAA',
      value: scores.wcag2AAA,
      fill: '#10b981', // Green
      lightFill: '#6ee7b7'
    },
    {
      name: 'Section 508',
      fullName: 'Section 508',
      value: scores.section508,
      fill: '#f59e0b', // Amber
      lightFill: '#fcd34d'
    }
  ];

  function getScoreColor(score: number): string {
    if (score >= 90) return '#10b981'; // Green
    if (score >= 70) return '#f59e0b'; // Amber
    if (score >= 50) return '#f97316'; // Orange
    return '#ef4444'; // Red
  }

  function getScoreGrade(score: number): string {
    if (score >= 90) return 'A';
    if (score >= 80) return 'B';
    if (score >= 70) return 'C';
    if (score >= 60) return 'D';
    return 'F';
  }

  function getScoreLabel(score: number): string {
    if (score >= 90) return 'Excellent';
    if (score >= 80) return 'Good';
    if (score >= 70) return 'Fair';
    if (score >= 60) return 'Poor';
    return 'Needs Work';
  }



  const scoreColor = getScoreColor(scores.overall);
  const scoreGrade = getScoreGrade(scores.overall);
  const scoreLabel = getScoreLabel(scores.overall);

  return (
    <div className="space-y-8">
      {/* Hero Score Section */}
      <div className="glass rounded-3xl p-8 relative overflow-hidden transition-all hover:shadow-2xl hover:shadow-primary-500/5 group border border-white/40">
        <div className="relative z-10">
          <div className="flex flex-col lg:flex-row items-center lg:items-center gap-8 lg:gap-10">
            {/* Main Circular Gauge */}
            <div className="relative w-36 h-36 md:w-40 md:h-40 lg:w-48 lg:h-48 flex-shrink-0 mx-auto lg:mx-0">
              <svg className="w-full h-full transform -rotate-90 filter drop-shadow-xl" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="46" fill="none" stroke={scoreColor} strokeWidth="1" strokeOpacity="0.2" />
                <circle cx="50" cy="50" r="42" fill="none" stroke="#f1f5f9" strokeWidth="10" />
                <circle
                  cx="50" cy="50" r="42"
                  fill="none"
                  stroke={scoreColor}
                  strokeWidth="10"
                  strokeDasharray="264"
                  strokeDashoffset={264 - (264 * scores.overall / 100)}
                  strokeLinecap="round"
                  className="transition-all duration-1000 ease-out"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                <span className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter" style={{ color: scoreColor }}>{scores.overall}</span>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Score</span>
              </div>
            </div>

            {/* Score Meta Info */}
            <div className="flex-1 space-y-6 w-full min-w-0">
              <div className="flex items-center gap-6">
                <div className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 rounded-2xl md:rounded-3xl font-black text-3xl md:text-5xl text-white shadow-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: scoreColor }}>
                  {scoreGrade}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl md:text-3xl lg:text-4xl font-black text-gray-900 leading-tight mb-1">{scoreLabel}</h3>
                  <p className="text-[10px] md:text-xs lg:text-sm font-bold text-gray-400 uppercase tracking-[0.2em] whitespace-nowrap">Accessibility Rating</p>
                </div>
              </div>

              <div className="p-5 md:p-6 bg-gray-50/80 rounded-2xl md:rounded-3xl border border-white/60 shadow-inner relative overflow-hidden group/text">
                <div className="absolute left-0 top-0 w-1.5 h-full transition-all group-hover/text:w-2" style={{ backgroundColor: scoreColor }} />
                <p className="text-sm md:text-base text-gray-600 leading-relaxed font-medium">
                  {scores.overall >= 90 && "Outstanding achievement! Your implementation exceeds industry standards for accessibility."}
                  {scores.overall >= 70 && scores.overall < 90 && "Solid foundation. A few more adjustments will place you among the top accessible websites."}
                  {scores.overall >= 50 && scores.overall < 70 && "Moderate accessibility detected. Some users may still face significant participation barriers."}
                  {scores.overall < 50 && "Critical accessibility gaps found. Immediate remediation is required to ensure basic inclusivity."}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Dynamic Background Blur */}
        <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full blur-[100px] opacity-10 group-hover:opacity-20 transition-opacity" style={{ backgroundColor: scoreColor }}></div>
      </div>

      {/* Standards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {standardsData.map((s, i) => (
          <div key={i} className="glass p-5 rounded-2xl border border-white/40 shadow-sm hover:shadow-md transition-all group">
            <div className="flex justify-between items-start mb-4">
              <div className="p-2 rounded-lg bg-gray-50 group-hover:bg-white transition-colors" style={{ color: s.fill }}>
                {i === 2 ? <Award className="w-5 h-5" /> : <TrendingUp className="w-5 h-5" />}
              </div>
              <div className="text-right">
                <span className="text-2xl font-black text-gray-900">{s.value}</span>
                <span className="text-[10px] font-bold text-gray-400 block uppercase">/ 100</span>
              </div>
            </div>
            <h4 className="font-bold text-gray-900 text-sm mb-3">{s.fullName}</h4>
            <div className="h-1.5 w-full bg-gray-100/50 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${s.value}%`, backgroundColor: s.fill }}
              ></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
